import {useState, useEffect, useRef} from 'react'
import {useBi, useEnvironment, useExperiments, useTranslation} from '@wix/yoshi-flow-editor'
import {Reservation, Status} from '@wix/ambassador-table-reservations-v1-reservation/types'

import {useReservationConfirmationStorage} from '../storage'
import {getReservationLocationById} from '../../../utils/reservationLocation'
import {getAreaNameById} from '../../../utils/areas'
import {useGetLayoutSize} from '../../../components-shared/LayoutSizeProvider'
import {getShouldShowAutoApproveText} from '../../../utils/getShouldShowAutoApproveText'
import {RequestStatus} from '../../../utils/wrapRequest'
import {
  CHECKOUT_PAGE_REFERRER,
  EXPERIMENTS,
  GoogleMapsConversionCollectExperimentOptions,
  RESERVATION_DETAILS_REFERRER,
} from '../../../utils/constants'
import {parseLocaleFromBM} from '../../../utils/locale'
import {usePaymentDetails} from '../../../utils/usePaymentDetails'
import {useExperiencePaymentDetails} from '../../../utils/useExperiencePaymentDetails'
import {isFreeOrder} from '../../../utils/ecom'
import {getLogger} from '../../../utils/getLogger'
import {
  getOrderCancellationFee,
  getOrderPaid,
  getOrderRefund,
  getOriginalPrepaidTotal,
} from '../../../utils/orderPayment'
import {getRwgToken} from '../../../utils/rwgToken'
import {triggerGoogleMapsConversionCollect} from '../../../services/googleService'
import {isProtectionActive} from '../../../utils/cancellationPolicy'
import {getAppliedCancellationFeeAmount} from '../../../utils/cancellationFeeState'
import {formatFeeAmount, getCancellationFeeWarning} from '../../../utils/cancellationFeeWarning'

import {getCalendarHref, getMessageType} from './utils'
import {STATUS_TO_KEYS_MAP_WITH_CANCELLATION_POLICY} from './constants'

const getUrlParam = (paramName: string): string | null => {
  if (typeof window === 'undefined') {
    return null
  }
  const urlParams = new URLSearchParams(window.location.search)

  return urlParams.get(paramName)
}

export const useHooks = () => {
  const {
    reservation,
    reservationLocations,
    orderDetails,
    orderId,
    experience,
    goToNewReservation,
    regionalSettings,
    approvalTextEditorState,
    isManualApproval,
    getReservationStatus,
    getReservationLocationsStatus,
    getOrderDetails,
    cancellationFeePreview,
    previewCancellationFee,
    trackTableReservedEvent,
    trackPurchaseEvent,
  } = useReservationConfirmationStorage()

  const bi = useBi()
  const logger = getLogger(bi)
  const {experiments} = useExperiments()
  const isCancellationFeeEnabled = experiments.enabled(EXPERIMENTS.tableReservationsCancellationFee)

  if (
    reservation?.status === Status.PAYMENT_PENDING ||
    reservation?.status === Status.PAYMENT_INFORMATION_PENDING
  ) {
    reservation.status = isManualApproval ? Status.REQUESTED : Status.RESERVED
  }

  const isReservationRequestFailed = getReservationStatus === RequestStatus.FAILED
  const isReservationLocationRequestFailed = getReservationLocationsStatus === RequestStatus.FAILED

  const shouldShowErrorModal = isReservationRequestFailed || isReservationLocationRequestFailed

  const {isViewer, isPreview} = useEnvironment()

  const {t} = useTranslation()
  const [isHydrated, setIsHydrated] = useState(false)
  const [isCancelDialogOpened, setIsCancelDialogOpened] = useState<boolean>(false)

  const isLoading = getReservationStatus === RequestStatus.LOADING

  const currentReservationLocation = getReservationLocationById(
    reservation?.details?.reservationLocationId ?? '',
    reservationLocations,
  )

  const titleCalendarEvent = t('uou-reservations.reservation-confirmation.calendar-title', {
    locationName: currentReservationLocation?.location?.name,
  })

  const openCancelDialog = () => {
    setIsCancelDialogOpened(true)
  }

  const closeCancelDialog = () => {
    setIsCancelDialogOpened(false)
  }

  const {layoutSize} = useGetLayoutSize()

  const shouldShowAutoApproveText = getShouldShowAutoApproveText(
    approvalTextEditorState,
    isManualApproval,
  )

  if (reservation) {
    reservation.status = isViewer
      ? reservation.status
      : shouldShowAutoApproveText
      ? Status.RESERVED
      : Status.REQUESTED
  }

  const reservationLocationPaymentDetails = usePaymentDetails(
    currentReservationLocation,
    reservation?.details?.partySize,
  )

  const experiencePaymentDetails = useExperiencePaymentDetails(
    experience,
    reservation?.details?.partySize,
  )

  const {isPerGuestPayment, pricePerUnitWithCurrency} = experience
    ? experiencePaymentDetails
    : reservationLocationPaymentDetails

  const reservationCancellationPolicy = reservation?.cancellationPolicy
  const hasCancellationPolicy = isProtectionActive(
    reservationCancellationPolicy,
    reservation?.details?.partySize,
  )

  const locale = parseLocaleFromBM(regionalSettings)
  const feeWarning = getCancellationFeeWarning({
    reservation,
    reservationLocation: currentReservationLocation,
    orderDetails,
    preview: cancellationFeePreview,
    locale,
  })
  const isCanceled = reservation?.status === Status.CANCELED
  const shouldShowChargedCancellationFee = Boolean(
    isCancellationFeeEnabled && isCanceled && feeWarning.feeAlreadyApplied,
  )
  const shouldShowPendingCancellationFee = Boolean(
    isCancellationFeeEnabled &&
      isCanceled &&
      !feeWarning.feeAlreadyApplied &&
      (feeWarning.feeApplies || (!feeWarning.hasPrepaidOrder && feeWarning.hasCapturedCard)),
  )
  const feeCurrency = feeWarning.currency ?? currentReservationLocation?.currency
  const orderCancellationFee = getOrderCancellationFee(orderDetails)
  const appliedCancellationFeeAmount = shouldShowChargedCancellationFee
    ? orderCancellationFee?.formattedAmount ??
      formatFeeAmount(orderCancellationFee?.amount, feeCurrency, locale) ??
      (feeWarning.hasPrepaidOrder
        ? feeWarning.formattedTotal
        : getAppliedCancellationFeeAmount(reservation, orderDetails, feeCurrency) ??
          feeWarning.formattedTotal)
    : shouldShowPendingCancellationFee
    ? formatFeeAmount('0', feeCurrency, locale)
    : undefined
  const prepaidTotalAmount = isCancellationFeeEnabled
    ? getOriginalPrepaidTotal(orderDetails)?.formattedAmount ??
      formatFeeAmount(getOriginalPrepaidTotal(orderDetails)?.amount, feeCurrency, locale)
    : undefined
  const orderPaidAmount = isCancellationFeeEnabled
    ? getOrderPaid(orderDetails)?.formattedAmount ??
      formatFeeAmount(getOrderPaid(orderDetails)?.amount, feeCurrency, locale)
    : undefined
  const orderRefundAmount = getOrderRefund(orderDetails)?.formattedAmount
  const shouldShowOrderDetails = Boolean(
    (orderDetails?.lineItems?.[0].quantity &&
      Number(orderDetails.lineItems[0].lineItemPrice?.amount) > 0) ||
      shouldShowPendingCancellationFee ||
      (shouldShowChargedCancellationFee && appliedCancellationFeeAmount),
  )

  const cancellationPolicySubtitleOverride =
    hasCancellationPolicy && reservation?.status
      ? STATUS_TO_KEYS_MAP_WITH_CANCELLATION_POLICY[reservation.status]?.subtitle
      : undefined

  const onAddToCalendarClick = () => {
    logger.clickOnAddToCalendar({
      reservation: reservation as Reservation,
      locationId: currentReservationLocation?.location?.id,
      reservationLocationId: currentReservationLocation?.id,
      isPreview,
      isSuccessful: true,
    })
  }

  const previousStatusRef = useRef<Status | undefined>(reservation?.status)
  const trackedConversionReservationId = useRef<string>()

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  useEffect(() => {
    const previousStatus = previousStatusRef.current

    previousStatusRef.current = reservation?.status

    if (
      reservation?.status !== Status.CANCELED ||
      !previousStatus ||
      previousStatus === Status.CANCELED
    ) {
      return
    }

    if (orderId && typeof getOrderDetails === 'function') {
      void getOrderDetails(orderId)
    }

    if (
      isCancellationFeeEnabled &&
      reservation.id &&
      typeof previewCancellationFee === 'function'
    ) {
      void previewCancellationFee(reservation.id)
    }
  }, [
    getOrderDetails,
    isCancellationFeeEnabled,
    orderId,
    previewCancellationFee,
    reservation?.id,
    reservation?.status,
  ])

  useEffect(() => {
    // Free reservation events will be sent in Step 2 after the reservation creation request
    if (!reservation?.id || trackedConversionReservationId.current === reservation.id) {
      return
    }

    if (orderDetails && !isFreeOrder(orderDetails)) {
      trackedConversionReservationId.current = reservation.id
      trackTableReservedEvent(reservation.id, reservation.reservee)
      trackPurchaseEvent(reservation.id, orderDetails)
    }

    const rwgToken = getRwgToken()
    const referrerParam = getUrlParam('referrer')
    const isReservationDetailsReferrer = referrerParam === RESERVATION_DETAILS_REFERRER
    const isCheckoutReferrer = referrerParam === CHECKOUT_PAGE_REFERRER

    if (rwgToken && (isReservationDetailsReferrer || isCheckoutReferrer)) {
      triggerGoogleMapsConversionCollect(
        rwgToken,
        GoogleMapsConversionCollectExperimentOptions.prod,
      )
    }
  }, [reservation?.id, orderDetails])

  return {
    t,
    isHydrated,
    layoutSize,
    isCancelDialogOpened,
    reservation,
    regionalSettings,
    orderDetails,
    shouldShowOrderDetails,
    experience,
    goToNewReservation,
    openCancelDialog,
    closeCancelDialog,
    calendarHref: getCalendarHref(titleCalendarEvent, currentReservationLocation, reservation),
    messageType: getMessageType(reservation?.reservee ?? ''),
    formattedAddress: currentReservationLocation?.location?.address?.formattedAddress ?? '',
    locationName: currentReservationLocation?.location?.name ?? '',
    areaName: getAreaNameById(currentReservationLocation, reservation?.details?.areaId),
    timeZone: currentReservationLocation?.location?.timeZone,
    phoneNumber: currentReservationLocation?.location?.phone,
    isLoading,
    isPerGuestPayment,
    pricePerUnitWithCurrency,
    onAddToCalendarClick,
    shouldShowErrorModal,
    reservationCancellationPolicy,
    hasCancellationPolicy,
    cancellationPolicySubtitleOverride,
    shouldShowPendingCancellationFee,
    shouldShowChargedCancellationFee,
    appliedCancellationFeeAmount,
    prepaidTotalAmount,
    orderPaidAmount,
    orderRefundAmount,
    isCancellationFeeEnabled,
    feeWarning,
  }
}
